import { useCallback, useEffect, useState } from "react";
import { Check, Download, FileText, Loader2, MoreHorizontal, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CreateLoaderTable } from "@/components/Loaders";
import { If } from "@/components/If";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import {
  approveBalanceAdjustmentRequest,
  getBalanceAdjustmentRequests,
  rejectBalanceAdjustmentRequest,
} from "@/Api/balanceAdjustmentRequests";
import {
  AppDialogContent,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { NewAdjustmentModal } from "./NewAdjustmentModal";
import {
  AdjustmentFilters,
  AdjustmentRequest,
  AdjustmentsResponse,
  AdjustmentStatus,
} from "./types";

const STATUS_LABEL: Record<AdjustmentStatus, string> = {
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Rejeitado",
};

const STATUS_STYLE: Record<AdjustmentStatus, string> = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const REASON_BADGE =
  "inline-block rounded px-2 py-1 text-xs font-bold uppercase leading-tight bg-[#F0A03A] text-gray-900";

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

type Review = { item: AdjustmentRequest; action: "approve" | "reject" } | null;

export const BalanceAdjustments = () => {
  const { hasPermission } = usePermissions();
  const [listData, setListData] = useState<AdjustmentsResponse>({
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AdjustmentFilters>({ status: "pending" });
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [detail, setDetail] = useState<AdjustmentRequest | null>(null);
  const [review, setReview] = useState<Review>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const fetchData = useCallback(async (current: AdjustmentFilters) => {
    setLoading(true);
    try {
      const { data } = await getBalanceAdjustmentRequests({
        ...current,
        status: current.status || undefined,
      });
      if (data?.response) setListData(data.response);
    } catch (error) {
      console.error("Erro ao buscar ajustes:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(filters);
  }, [fetchData, filters]);

  const goToPage = (page: number) => setFilters((prev) => ({ ...prev, page }));

  const closeReview = () => {
    setReview(null);
    setReviewNotes("");
  };

  const handleReview = async () => {
    if (!review) return;

    try {
      setReviewing(true);
      const { ok } =
        review.action === "approve"
          ? await approveBalanceAdjustmentRequest(review.item.uuid)
          : await rejectBalanceAdjustmentRequest(review.item.uuid, reviewNotes.trim());

      // O motivo do erro já é exibido pelo interceptor da API (Api/index.ts);
      // um toast genérico aqui só empilhava uma segunda mensagem sem informação.
      if (!ok) return;

      toast.success(
        review.action === "approve"
          ? "Ajuste aprovado e saldo atualizado!"
          : "Ajuste rejeitado."
      );
      closeReview();
      fetchData(filters);
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao processar a solicitação.");
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1 w-full sm:w-56">
          <Label>Status</Label>
          <Select
            value={filters.status || "all"}
            onValueChange={(value) =>
              setFilters({ status: value === "all" ? "" : (value as AdjustmentStatus) })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="approved">Aprovados</SelectItem>
              <SelectItem value="rejected">Rejeitados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <If condition={hasPermission("request-balance-adjustments")}>
          <Button className="gap-2" onClick={() => setNewModalOpen(true)}>
            <Plus className="h-4 w-4" />
            Novo Ajuste
          </Button>
        </If>
      </div>

      {loading ? (
        <CreateLoaderTable cols={9} lines={6} />
      ) : (
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Acréscimo/Redução</TableHead>
              <TableHead>Criado por</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listData.data.length > 0 ? (
              listData.data.map((item) => {
                const isAdd = item.type === "add";
                return (
                  <TableRow key={item.uuid}>
                    <TableCell className="font-medium">{item.client?.name}</TableCell>
                    <TableCell>
                      {item.reason && (
                        <span className={REASON_BADGE}>{item.reason.name}</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <button
                        type="button"
                        className="text-left truncate w-full hover:underline"
                        title="Ver detalhes"
                        onClick={() => setDetail(item)}
                      >
                        {item.description}
                      </button>
                    </TableCell>
                    <TableCell className={cn("font-semibold", isAdd ? "text-green-600" : "text-red-600")}>
                      {isAdd ? "+ " : "- "}
                      {Mask.formatCurrency(Number(item.amount))}
                    </TableCell>
                    <TableCell>
                      <span className={cn("px-2 py-1 rounded text-xs font-semibold", STATUS_STYLE[item.status])}>
                        {STATUS_LABEL[item.status]}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "px-2 py-1 rounded text-xs font-semibold",
                          isAdd ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        )}
                      >
                        {isAdd ? "Acréscimo" : "Redução"}
                      </span>
                    </TableCell>
                    <TableCell>{item.requester?.name}</TableCell>
                    <TableCell>{formatDateTime(item.created_at)}</TableCell>
                    <TableCell className="text-center">
                      {item.status === "pending" ? (
                        <If condition={hasPermission("approve-balance-adjustments")}>
                          <div className="flex justify-center gap-1">
                            <Button
                              size="sm"
                              className="h-8 gap-1 bg-green-600 hover:bg-green-700"
                              onClick={() => setReview({ item, action: "approve" })}
                            >
                              <Check className="h-4 w-4" />
                              Aprovar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 gap-1 bg-white border-red-300 text-red-600 hover:bg-red-50"
                              onClick={() => setReview({ item, action: "reject" })}
                            >
                              <X className="h-4 w-4" />
                              Rejeitar
                            </Button>
                          </div>
                        </If>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn("h-8", appDialogSecondaryButton)}
                          onClick={() => setDetail(item)}
                        >
                          Detalhes
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  Nenhum ajuste encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {listData.last_page > 1 && (
        <div className="flex justify-between mb-3 mt-1">
          <span className="font-medium">
            Exibindo {listData.current_page} de um total de {listData.last_page}
          </span>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={(e) => {
                    e.preventDefault();
                    if (listData.current_page > 1) goToPage(listData.current_page - 1);
                  }}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {listData.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={(e) => {
                    e.preventDefault();
                    goToPage(listData.last_page);
                  }}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={(e) => {
                    e.preventDefault();
                    if (listData.current_page < listData.last_page) {
                      goToPage(listData.current_page + 1);
                    }
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <NewAdjustmentModal
        open={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        onCreated={() => {
          setNewModalOpen(false);
          fetchData(filters);
        }}
      />

      <Dialog open={!!review} onOpenChange={(open) => !open && closeReview()}>
        <DialogContent className="sm:max-w-md p-0 sm:p-0 overflow-hidden gap-0">
          {review && (
            <>
              <div
                className={cn(
                  "flex flex-col items-center gap-3 px-6 pt-8 pb-5 text-center",
                  review.action === "approve"
                    ? "bg-green-50 dark:bg-green-950/30"
                    : "bg-red-50 dark:bg-red-950/30"
                )}
              >
                <div
                  className={cn(
                    "flex h-14 w-14 items-center justify-center rounded-full",
                    review.action === "approve"
                      ? "bg-green-100 text-green-600"
                      : "bg-red-100 text-red-600"
                  )}
                >
                  {review.action === "approve" ? (
                    <Check className="h-7 w-7" />
                  ) : (
                    <X className="h-7 w-7" />
                  )}
                </div>
                <DialogHeader className="space-y-1 sm:text-center">
                  <DialogTitle className="text-xl">
                    {review.action === "approve" ? "Aprovar ajuste" : "Rejeitar ajuste"}
                  </DialogTitle>
                  <DialogDescription>
                    {review.action === "approve"
                      ? "Ao aprovar, o valor será lançado no saldo do cliente."
                      : "O saldo do cliente não será alterado."}
                  </DialogDescription>
                </DialogHeader>
              </div>

              <div className="space-y-4 px-6 py-5">
                <div className="rounded-lg border bg-zinc-50 dark:bg-zinc-900/40 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500">Cliente</span>
                    <span className="text-sm font-semibold text-right">
                      {review.item.client?.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500">Motivo</span>
                    <span className="text-sm font-medium text-right">
                      {review.item.reason?.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 border-t pt-3">
                    <span className="text-sm text-gray-500">
                      {review.item.type === "add" ? "Acréscimo" : "Redução"}
                    </span>
                    <span
                      className={cn(
                        "text-2xl font-bold",
                        review.item.type === "add" ? "text-green-600" : "text-red-600"
                      )}
                    >
                      {review.item.type === "add" ? "+ " : "- "}
                      {Mask.formatCurrency(Number(review.item.amount))}
                    </span>
                  </div>
                </div>

                {review.action === "reject" && (
                  <div className="space-y-2">
                    <Label htmlFor="review-notes">Observação (opcional)</Label>
                    <Textarea
                      id="review-notes"
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Explique por que o ajuste foi rejeitado..."
                      maxLength={1000}
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                )}

                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    variant="outline"
                    className={cn("flex-1", appDialogSecondaryButton)}
                    onClick={closeReview}
                    disabled={reviewing}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleReview}
                    disabled={reviewing}
                    className={cn(
                      "flex-1 text-white",
                      review.action === "approve"
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-red-600 hover:bg-red-700"
                    )}
                  >
                    {reviewing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {review.action === "approve" ? "Confirmar aprovação" : "Confirmar rejeição"}
                  </Button>
                </DialogFooter>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <AppDialogContent icon={FileText} title="Detalhes do Ajuste">
          {detail && (
            <div className="space-y-4 text-sm px-6 py-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-500">Cliente</label>
                  <p className="font-semibold">{detail.client?.name}</p>
                </div>
                <div>
                  <label className="text-gray-500">Motivo</label>
                  <p>
                    {detail.reason && (
                      <span className={REASON_BADGE}>{detail.reason.name}</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-gray-500">Valor</label>
                  <p className={cn("font-bold", detail.type === "add" ? "text-green-600" : "text-red-600")}>
                    {detail.type === "add" ? "+ " : "- "}
                    {Mask.formatCurrency(Number(detail.amount))}
                  </p>
                </div>
                <div>
                  <label className="text-gray-500">Status</label>
                  <p>
                    <span className={cn("px-2 py-1 rounded text-xs font-semibold", STATUS_STYLE[detail.status])}>
                      {STATUS_LABEL[detail.status]}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-gray-500">Criado por</label>
                  <p>{detail.requester?.name}</p>
                </div>
                <div>
                  <label className="text-gray-500">Data</label>
                  <p>{formatDateTime(detail.created_at)}</p>
                </div>
                {detail.reviewer && detail.reviewed_at && (
                  <>
                    <div>
                      <label className="text-gray-500">
                        {detail.status === "approved" ? "Aprovado por" : "Rejeitado por"}
                      </label>
                      <p>{detail.reviewer.name}</p>
                    </div>
                    <div>
                      <label className="text-gray-500">Em</label>
                      <p>{formatDateTime(detail.reviewed_at)}</p>
                    </div>
                  </>
                )}
              </div>

              <div className="pt-3 border-t">
                <label className="text-gray-500">Descrição</label>
                <p className="mt-1 whitespace-pre-wrap">{detail.description}</p>
              </div>

              {detail.review_notes && (
                <div className="pt-3 border-t">
                  <label className="text-gray-500">Observação da rejeição</label>
                  <p className="mt-1 whitespace-pre-wrap">{detail.review_notes}</p>
                </div>
              )}

              {detail.files.length > 0 && (
                <div className="pt-3 border-t space-y-2">
                  <label className="text-gray-500">Comprovantes</label>
                  {detail.files.map((file, index) => (
                    <Button
                      key={file.uuid}
                      variant="outline"
                      className={cn("w-full justify-start", appDialogSecondaryButton)}
                      onClick={() => window.open(file.file, "_blank")}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Arquivo {index + 1}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </AppDialogContent>
      </Dialog>
    </div>
  );
};
